import { useCallback, useMemo, useState } from 'react';

import { Button, Divider, FlexAligner, LoadingBubbles, Pulse, Typography } from '@scaleaq/scaleui';
import { AutomationRule } from '@scaleaq/sitecontrol-lib';
import { spacing } from '@scaleaq/sui-tokens/responsive';
import { useTranslation } from 'react-i18next';

import { GroupBorder } from '@/components/automationRulesForInventory/automationRulesForInventory.style.ts';
import { useActivateAutomation, useDeactivateAutomation } from '@/dataAccess/rest';
import { Icon } from '@/utils';

type RulesProps = {
  ingressDescription: string;
  onUpdateStatusError: (error: { message: string }) => void;
  onUpdateStatusSuccess: (name: string, isActive: boolean) => void;
  inventoryName?: string;
  rules: AutomationRule[];
};

/**
 * @component AutomationRulesForInventory
 * @description Displays a list of automation rules related to an inventory, allowing users
 * to activate or deactivate the rule.
 * Shows the number of rules, highlights number of active rules and provides a toggle button
 * for each rule to activate or deactivate it.
 * @param ingressDescription - A description of the ingress relation to the rules.
 * e.g. 'This inventory', or 'The parent inventory'
 * @param onUpdateStatusSuccess
 * @param onUpdateStatusError
 * @param rules - list of automation rules related to the inventory
 * @param inventoryName - Optional name of the inventory related to the rules indicated in ingressDescription.
 * @constructor
 */
export const AutomationRulesForInventory = ({
  ingressDescription,
  onUpdateStatusSuccess,
  onUpdateStatusError,
  rules,
  inventoryName,
}: RulesProps) => {
  const { t } = useTranslation('automation');
  const activeRulesCount = useMemo(() => rules.filter(item => item.active).length, [rules]);

  const { mutate: deactivateAutomation, isPending: deactivateAutomationLoading } =
    useDeactivateAutomation();
  const { mutate: activateAutomation, isPending: updateAutomationLoading } =
    useActivateAutomation();

  const [ruleUpdating, setRuleUpdating] = useState<string | undefined>();

  const onToggleActiveStatus = useCallback(
    (a: AutomationRule) => {
      setRuleUpdating(a.id);
      const update: AutomationRule = {
        ...a,
        active: !a.active,
      };
      if (update.active) {
        activateAutomation(update, {
          onSuccess: () => {
            onUpdateStatusSuccess(update.name, update.active);
            setRuleUpdating(undefined);
          },
          onError: (error: { message: string }) => {
            onUpdateStatusError(error);
            setRuleUpdating(undefined);
          },
        });
      } else {
        deactivateAutomation(update, {
          onSuccess: () => {
            onUpdateStatusSuccess(update.name, update.active);
            setRuleUpdating(undefined);
          },
          onError: (error: { message: string }) => {
            onUpdateStatusError(error);
            setRuleUpdating(undefined);
          },
        });
      }
    },
    [activateAutomation, onUpdateStatusSuccess, onUpdateStatusError, deactivateAutomation]
  );

  if (rules.length > 0) {
    return (
      <GroupBorder>
        <FlexAligner gap='0.3rem'>
          <Typography weight={800} type='body_large'>
            {ingressDescription}
          </Typography>
          {inventoryName && <Typography type='body_large'>{`(${inventoryName})`}</Typography>}
          <Typography type='body_large'>{t('relatedRules', { count: rules.length })}</Typography>
        </FlexAligner>
        <Typography type='body_large'>{t('activeRules', { count: activeRulesCount })}</Typography>
        <div style={{ paddingBottom: spacing['medium'] }}>
          <Divider />
        </div>
        {rules.map((a: AutomationRule) => (
          <FlexAligner key={a.id} spaceBetween alignment='center'>
            <Typography style={{ width: '100%' }}>{a.name}</Typography>
            <FlexAligner alignment='center-right' gap={spacing['large']}>
              {a.active && (
                <Pulse $pulse={true} $behavior='Even' $pulseSpeed={3}>
                  <Icon.Automation16 />
                </Pulse>
              )}
              {a.id === ruleUpdating &&
                (deactivateAutomationLoading || updateAutomationLoading) && <LoadingBubbles />}
              <Button
                style={{ width: '6rem', border: '0' }}
                variant='outline'
                label={t(a.active ? 'deactivate' : 'activate')}
                color={a.active ? 'caution' : 'success'}
                onClick={() => onToggleActiveStatus(a)}
              />
            </FlexAligner>
          </FlexAligner>
        ))}
      </GroupBorder>
    );
  }
};
